/**
 * @fileoverview Bundle Item Web Component for Shopify product bundles
 * @description Handles bundle selection, quantity management, and pricing calculations.
 * Receives inventory updates from component-inventory-manager.js
 * Falls back to basic functionality for browsers without Custom Elements support
 * @requires component-inventory-manager.js
 */

// ============================================================================
// FEATURE DETECTION
// ============================================================================
(function () {
  'use strict';

  // Check if browser supports Custom Elements API
  const hasCustomElementsSupport =
    'customElements' in window && 'HTMLElement' in window && typeof window.HTMLElement === 'function';

  if (hasCustomElementsSupport) {
    // ========================================================================
    // MODERN BROWSER PATH - WEB COMPONENTS
    // ========================================================================
    /** @const {Object} Configuration constants for bundle behavior */
    const BUNDLE_CONSTANTS = {
      // Bundle Types
      CUSTOM_BUNDLE: 0,
      SINGLE_UNIT: 1,

      // Limits
      MIN_QUANTITY: 1,
      MIN_CUSTOM_VISIBLE: 2, // Custom bundle shows when > 1 available
      DEFAULT_INCREMENT: 1, // Default quantity increment for smart defaults

      // Timing
      UPDATE_DELAY: 10,
      LIMIT_ANIMATION_DURATION: 600,
      MESSAGE_DISPLAY_DURATION: 5000,

      // CSS Classes
      SELECTED_CLASS: 'bundles__bundle-item--selected',
      COLLAPSED_CLASS: 'collapsed',
      LIMIT_HIT_CLASS: 'bundle-item--limit-hit',
      HAS_BADGE_CLASS: 'bundles__bundle-item--has-badge',
      HIDDEN_CLASS: 'hidden',

      // DOM Selectors
      CUSTOM_INPUT_SELECTOR: '.bundle-item__custom-input',
      PRICE_DISPLAY_SELECTOR: '[role="dynamic-price"]',
      SAVINGS_DISPLAY_SELECTOR: '[role="saving-value"]',
      SUBTITLE_SELECTOR: '[role="subtitle"]',
      LABEL_SELECTOR: '.bundle-item__label',
      MAX_MESSAGE_SELECTOR: '.bundle-item__max-message',
      UNIT_SELECTOR: '.bundle-item__unit',
      SAVINGS_WRAPPER_SELECTOR: '.bundle-item__savings',
      CUSTOM_PRICE_SELECTOR: '.bundle-item__price.bundle-item__price--custom',
      MAX_TEXT_SELECTOR: '.bundle-item__max-text',
      CUSTOM_BUNDLES_SELECTOR: 'bundle-item[data-number="0"]',
      ALL_BUNDLES_SELECTOR: 'bundle-item',
      CUSTOM_CLASS: 'bundles__bundle-item--custom',
      CONTAINER_SELECTOR: '.bundles-container',

      // Badge Classes
      BADGE_CLASS: 'bundle-badge',
      BADGE_DYNAMIC_CLASS: 'bundle-badge--dynamic',
      BADGE_BEST_DEAL_CLASS: 'bundle-badge--best-deal',
      BADGE_MOST_POPULAR_CLASS: 'bundle-badge--most-popular',
      BADGE_TEXT_CLASS: 'bundle-badge__text',

      // Events
      INVENTORY_UPDATE_EVENT: 'product-inventory-update',
      SELECTION_INVALID_EVENT: 'bundle-selection-invalid',

      // Price Calculation
      PRICE_DIVISOR: 100, // Convert cents to dollars

      // Component
      COMPONENT_NAME: 'bundle-item',

      // Button selectors
      QUANTITY_BUTTONS_SELECTOR: 'button[name="plus"], button[name="minus"]',
      QUANTITY_LABEL_SELECTOR: 'span[role="label"]'
    };

    /**
     * Custom element for individual bundle options
     * @extends HTMLElement
     */
    class BundleItem extends HTMLElement {
      /**
       * Check if this is a custom quantity bundle
       * @returns {boolean} True if bundle size is 0 (custom)
       */
      get isCustomBundle() {
        return this.bundleSize === BUNDLE_CONSTANTS.CUSTOM_BUNDLE;
      }

      /**
       * Lazy-loaded DOM element cache
       * @returns {Object} Cached DOM elements
       */
      get elements() {
        if (!this._elementsCache) {
          this._elementsCache = this._queryElements();
        }
        return this._elementsCache;
      }

      /**
       * Query and cache DOM elements
       * @private
       * @returns {Object} DOM element references
       */
      _queryElements() {
        return {
          customInput: this.querySelector(BUNDLE_CONSTANTS.CUSTOM_INPUT_SELECTOR),
          priceDisplay: this.querySelector(BUNDLE_CONSTANTS.PRICE_DISPLAY_SELECTOR),
          savingsDisplay: this.querySelector(BUNDLE_CONSTANTS.SAVINGS_DISPLAY_SELECTOR),
          subtitle: this.querySelector(BUNDLE_CONSTANTS.SUBTITLE_SELECTOR),
          label: this.querySelector(BUNDLE_CONSTANTS.LABEL_SELECTOR),
          maxMessage: this.querySelector(BUNDLE_CONSTANTS.MAX_MESSAGE_SELECTOR),
          unitEl: this.querySelector(BUNDLE_CONSTANTS.UNIT_SELECTOR),
          savingsWrapper: this.querySelector(BUNDLE_CONSTANTS.SAVINGS_DISPLAY_SELECTOR)?.closest(
            BUNDLE_CONSTANTS.SAVINGS_WRAPPER_SELECTOR
          ),
          customPriceSpan: this.querySelector(BUNDLE_CONSTANTS.CUSTOM_PRICE_SELECTOR),
          maxText: this.querySelector(BUNDLE_CONSTANTS.MAX_MESSAGE_SELECTOR)?.querySelector(
            BUNDLE_CONSTANTS.MAX_TEXT_SELECTOR
          )
        };
      }

      /**
       * Clear element cache when DOM changes
       * @private
       */
      _clearElementsCache() {
        this._elementsCache = null;
      }

      /**
       * Hide DOM element
       * @private
       * @param {HTMLElement} element - Element to hide
       */
      _hideElement(element) {
        if (element) {
          element.style.display = 'none';
        }
      }

      /**
       * Show DOM element
       * @private
       * @param {HTMLElement} element - Element to show
       */
      _showElement(element) {
        if (element) {
          element.style.display = 'block';
        }
      }

      /**
       * Check element visibility
       * @private
       * @param {HTMLElement} element - Element to check
       * @returns {boolean} True if visible
       */
      _isElementVisible(element) {
        return element && element.style.display !== 'none';
      }

      /**
       * Get data-number attribute as integer
       * @private
       * @param {HTMLElement} element
       * @returns {number}
       */
      _getDataNumber(element) {
        return parseInt(element.getAttribute('data-number'), 10) || 0;
      }

      _getDataPrice(element) {
        return parseInt(element.getAttribute('data-price'), 10) || 0;
      }

      _getDataProductPrice(element) {
        return parseFloat(element.getAttribute('data-product-price')) || 0;
      }

      _getDataCurrency(element) {
        return element.getAttribute('data-currency') || '';
      }

      /**
       * Initialize bundle item component
       */
      constructor() {
        super();
        this.addEventListener('click', this.onBundleItemClick.bind(this));
        this.sectionId = this.getAttribute('section-id');
        this.bundleSize = this._getDataNumber(this);
        this.sharedInput = document.querySelector(`#quantity-picker-bundles-${this.sectionId}`);

        this.productId = this.getAttribute('data-product-id');
        this.variantId = this.getAttribute('data-product-variant-id');
        this.productHandle = this.getAttribute('data-product-handle');
        this.inventoryQuantity = 0;
        this.cartQuantity = 0;
        this.maxAvailable = 0;

        // Add click debounce timer
        this._clickDebounceTimer = null;

        if (this.isCustomBundle) {
          this.savedCustomValue = 0;
        }
        if (this.isCustomBundle && this.hasAttribute('data-custom-default')) {
          this.selectedQuantity = parseInt(this.getAttribute('data-custom-default'));
        } else {
          this.selectedQuantity = this.bundleSize || BUNDLE_CONSTANTS.MIN_QUANTITY;
        }

        this._handleInventoryUpdate = this._handleInventoryUpdate.bind(this);
      }

      /**
       * Update selected quantity and all dependent UI
       * @param {number} value - New quantity value
       * @param {Object} options - Update options
       * @param {boolean} [options.skipValidation=false] - Skip min/max validation
       * @param {boolean} [options.updateSharedInput=true] - Update shared form input
       * @param {string} [options.source='user'] - Source of change (user/auto/inventory)
       */
      setSelectedQuantity(value, options = {}) {
        const { skipValidation = false, updateSharedInput = true, source = 'user' } = options;

        const newValue = skipValidation
          ? value
          : Math.max(BUNDLE_CONSTANTS.MIN_QUANTITY, Math.min(value, this.maxAvailable || value));
        if (this.selectedQuantity === newValue) return;

        const oldValue = this.selectedQuantity;
        this.selectedQuantity = newValue;

        this._onQuantityChanged(oldValue, newValue, { updateSharedInput, source });
      }

      /**
       * Handle side effects of quantity change
       * @private
       * @param {number} oldValue - Previous quantity
       * @param {number} newValue - New quantity
       * @param {Object} options - Change options
       */
      _onQuantityChanged(oldValue, newValue, options = {}) {
        const { updateSharedInput = true } = options;
        if (updateSharedInput && this.sharedInput && this.classList.contains(BUNDLE_CONSTANTS.SELECTED_CLASS)) {
          this.sharedInput.value = newValue;
        }

        // Update visual display
        if (this.quantityDisplay) {
          this.quantityDisplay.textContent = newValue;
        }

        // Update other dependent UI
        this._updateControls();
        this._updateSavings(newValue);

        // Only update price if this bundle is selected
        if (this.classList.contains(BUNDLE_CONSTANTS.SELECTED_CLASS)) {
          this._updateDynamicPrice();
        }

        // Save value for custom bundle
        if (this.isCustomBundle) {
          this.savedCustomValue = newValue;
        }
      }

      /**
       * Web component lifecycle - element added to DOM
       */
      connectedCallback() {
        // Mark start of bundle initialization
        performance.mark('bundle-item-init-start');

        const init = () => {
          const initialInventory = parseInt(this.getAttribute('data-initial-inventory') || '999');

          this.inventoryQuantity = initialInventory;
          this.maxAvailable = initialInventory;
          this.cartQuantity = 0;

          this._setCustomInputHeight();
          this._setupQuantityControls();
          this._setupInventoryListener();

          this._updateVisibility();
          this._updateDynamicBadges();

          // Initialize state for pre-selected bundles (e.g., "1 unit" selected by default in HTML)
          if (this.classList.contains(BUNDLE_CONSTANTS.SELECTED_CLASS)) {
            this.updateInputValue(); // This calls _updateDynamicPrice() internally at line 738
          }

          // Mark completion of bundle initialization
          performance.mark('bundle-item-init-complete');

          // Measure the initialization time
          performance.measure('bundle-item-init', 'bundle-item-init-start', 'bundle-item-init-complete');
        };

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', init);
        } else {
          init();
        }
      }

      /**
       * Web component lifecycle - element removed from DOM
       */
      disconnectedCallback() {
        document.removeEventListener(BUNDLE_CONSTANTS.INVENTORY_UPDATE_EVENT, this._handleInventoryUpdate);
      }

      /**
       * Set up inventory update listener
       * @private
       */
      _setupInventoryListener() {
        document.addEventListener(BUNDLE_CONSTANTS.INVENTORY_UPDATE_EVENT, this._handleInventoryUpdate);
      }

      /**
       * Handle inventory update events
       * @private
       * @param {CustomEvent} event - Inventory update event
       */
      _handleInventoryUpdate(event) {
        if (event.detail.variantId === this.variantId) {
          // Mark start of inventory update processing
          performance.mark('bundle-item-inventory-update-start');

          this.inventoryQuantity = event.detail.inventoryQuantity;
          this.cartQuantity = event.detail.cartQuantity;
          this.maxAvailable = event.detail.maxAvailable;

          this._updateMaxAvailable();
          this._updateVisibility();
          this._updateDynamicBadges();

          // Mark end of inventory update processing
          performance.mark('bundle-item-inventory-update-complete');

          // Measure the update time
          performance.measure(
            'bundle-item-inventory-update',
            'bundle-item-inventory-update-start',
            'bundle-item-inventory-update-complete'
          );
        }
      }

      /**
       * Determine if bundle should be visible
       * @private
       * @returns {boolean} Visibility state
       */
      _shouldBeVisible() {
        if (this.inventoryQuantity === 0) {
          return false;
        }

        if (this.isCustomBundle) {
          return this._shouldCustomBundleBeVisible();
        } else {
          return this._shouldRegularBundleBeVisible();
        }
      }

      /**
       * Check if custom bundle should be visible
       * @private
       * @returns {boolean}
       */
      _shouldCustomBundleBeVisible() {
        return this.maxAvailable >= BUNDLE_CONSTANTS.MIN_CUSTOM_VISIBLE;
      }

      /**
       * Check if regular bundle should be visible
       * @private
       * @returns {boolean}
       */
      _shouldRegularBundleBeVisible() {
        if (this.bundleSize === BUNDLE_CONSTANTS.SINGLE_UNIT) {
          return true;
        }

        // No inventory available - hide
        if (this.maxAvailable === 0) {
          return false;
        }

        // Show if bundle size fits within available inventory
        return this.bundleSize <= this.maxAvailable;
      }

      _updateVisibility() {
        const shouldShow = this._shouldBeVisible();

        if (shouldShow) {
          // Remove hiding attributes/classes - following theme pattern
          this.removeAttribute('aria-hidden');
          this.classList.remove('hidden'); // Theme compatibility
          this.removeAttribute('inert'); // Modern browsers support this

          // Re-enable buttons only if browser doesn't support inert
          if (!('inert' in this)) {
            if (this.buttons) {
              this.buttons.forEach(btn => {
                btn.removeAttribute('tabindex');
                btn.removeAttribute('aria-disabled');
              });
            }
          }
        } else {
          // Hide element - following theme pattern (aria-hidden + .hidden class)
          this.setAttribute('aria-hidden', 'true');
          this.classList.add('hidden'); // Theme compatibility

          // Use 'inert' attribute if supported (makes entire subtree non-interactive)
          if ('inert' in this) {
            this.setAttribute('inert', '');
          } else {
            // Fallback for older browsers
            if (this.buttons) {
              this.buttons.forEach(btn => {
                btn.setAttribute('tabindex', '-1');
                btn.setAttribute('aria-disabled', 'true');
              });
            }
          }

          if (this.classList.contains(BUNDLE_CONSTANTS.SELECTED_CLASS)) {
            this._notifySelectionInvalid();
          }
        }

        // Update custom values when visibility changes
        setTimeout(() => {
          document.querySelectorAll(BUNDLE_CONSTANTS.CUSTOM_BUNDLES_SELECTOR).forEach(item => {
            item._updateCustomDefaultValue();
          });
        }, BUNDLE_CONSTANTS.UPDATE_DELAY);
      }

      _notifySelectionInvalid() {
        // Dispatch event to let container know selection is no longer valid
        const event = new CustomEvent(BUNDLE_CONSTANTS.SELECTION_INVALID_EVENT, {
          bubbles: true,
          detail: { bundleItem: this }
        });
        this.dispatchEvent(event);
      }

      _getMaxBundleQuantity() {
        let maxQuantity = 0;
        document.querySelectorAll(BUNDLE_CONSTANTS.ALL_BUNDLES_SELECTOR).forEach(item => {
          const itemNumber = this._getDataNumber(item);
          // Only consider visible items and non-custom items
          if (itemNumber > 0 && this._isElementVisible(item) && itemNumber <= item.maxAvailable) {
            maxQuantity = Math.max(maxQuantity, itemNumber);
          }
        });
        return maxQuantity;
      }

      onBundleItemClick() {
        // Don't allow selection if bundle shouldn't be visible (e.g., zero inventory)
        if (!this._shouldBeVisible()) {
          return;
        }

        // Don't process if already selected
        if (this.classList.contains(BUNDLE_CONSTANTS.SELECTED_CLASS)) {
          return;
        }

        // Debounce rapid clicks to prevent animation pile-up
        if (this._clickDebounceTimer) {
          return;
        }

        this._clickDebounceTimer = setTimeout(() => {
          this._clickDebounceTimer = null;
        }, 100); // 100ms debounce

        // Batch DOM updates in next animation frame for better performance
        requestAnimationFrame(() => {
          // Route to appropriate handler based on bundle type
          if (this.isCustomBundle) {
            this._handleCustomBundleSelection();
          } else {
            this._handleRegularBundleSelection();
          }
        });
      }

      /**
       * Handle selection of custom bundle (quantity selector)
       */
      _handleCustomBundleSelection() {
        // Hide any visible max message from previous custom bundle
        this._hideMaxMessage();

        // Deselect all bundles and select this one
        this._deselectAllBundles();
        this._selectBundle();

        // Expand custom controls
        this._expandCustomControls();

        // Set smart default quantity
        this._setSmartDefaultQuantity();

        // Update pricing
        this._updateDynamicPrice();
      }

      /**
       * Handle selection of regular bundle (fixed quantity)
       */
      _handleRegularBundleSelection() {
        // Deselect all bundles and select this one
        this._deselectAllBundles();
        this._selectBundle();

        // Update input with fixed bundle size
        this.updateInputValue();
      }

      /**
       * Hide max message from any custom bundle
       */
      _hideMaxMessage() {
        const previousCustom = document.querySelector(
          '.' + BUNDLE_CONSTANTS.CUSTOM_CLASS + '.' + BUNDLE_CONSTANTS.SELECTED_CLASS
        );
        if (previousCustom && previousCustom.elements) {
          previousCustom._hideElement(previousCustom.elements.maxMessage);
        }
      }

      /**
       * Deselect all bundles and save custom bundle state
       */
      _deselectAllBundles() {
        document.querySelectorAll(BUNDLE_CONSTANTS.ALL_BUNDLES_SELECTOR).forEach(item => {
          // Save custom bundle value before deselecting
          if (item.isCustomBundle && item.classList.contains(BUNDLE_CONSTANTS.SELECTED_CLASS)) {
            item.savedCustomValue = item.selectedQuantity;

            // Clear max message timeout when deselecting custom bundle
            if (item._messageTimeout) {
              clearTimeout(item._messageTimeout);
              item._messageTimeout = null;
              // Hide the max message immediately
              if (item.elements && item.elements.maxMessage) {
                item._hideElement(item.elements.maxMessage);
              }
            }
          }

          item.classList.remove(BUNDLE_CONSTANTS.SELECTED_CLASS);

          // Collapse any custom inputs
          if (item.elements.customInput) {
            item.elements.customInput.classList.add(BUNDLE_CONSTANTS.COLLAPSED_CLASS);
          }
        });
      }

      /**
       * Select this bundle
       */
      _selectBundle() {
        this.classList.add(BUNDLE_CONSTANTS.SELECTED_CLASS);
      }

      /**
       * Expand custom quantity controls
       */
      _expandCustomControls() {
        if (this.elements.customInput) {
          this.elements.customInput.classList.remove(BUNDLE_CONSTANTS.COLLAPSED_CLASS);
        }
      }

      /**
       * Set smart default quantity for custom bundle
       */
      _setSmartDefaultQuantity() {
        const maxBundleQuantity = this._getMaxBundleQuantity();
        const defaultValue = maxBundleQuantity + BUNDLE_CONSTANTS.DEFAULT_INCREMENT;

        // Use the stored custom value if it exists
        const storedValue = this.savedCustomValue || 0;

        // Use the higher of: stored value or default value
        // But never exceed max available
        let newValue = Math.max(storedValue, defaultValue);
        if (this.maxAvailable > 0) {
          newValue = Math.min(newValue, this.maxAvailable);
        }

        // Use the consolidated method to update quantity
        this.setSelectedQuantity(newValue, { updateSharedInput: true });
      }

      _updateDynamicPrice() {
        const priceInfo = this._calculatePrice();
        this._displayPrice(priceInfo);
      }

      /**
       * Calculate price based on bundle type
       */
      _calculatePrice() {
        return this.isCustomBundle ? this._calculateCustomBundlePrice() : this._calculateRegularBundlePrice();
      }

      /**
       * Display calculated price in UI
       */
      _displayPrice(priceInfo) {
        const currency = this._getDataCurrency(this);
        this._updatePriceDisplays(priceInfo, currency);
      }

      /**
       * Calculate price for custom bundle
       */
      _calculateCustomBundlePrice() {
        const quantity = this.selectedQuantity || 0;
        const defaultPrice = this._getDataPrice(this);

        // Find best matching tier
        const bestTier = this._findBestPriceTier(quantity);

        // Use tier price if found, otherwise use default
        const priceInCents = bestTier ? bestTier.price : defaultPrice;
        const unitPrice = priceInCents / BUNDLE_CONSTANTS.PRICE_DIVISOR;

        return {
          quantity: quantity,
          unitPrice: unitPrice,
          totalPrice: unitPrice * quantity,
          isCustom: true,
          tier: bestTier
        };
      }

      /**
       * Calculate price for regular bundle
       */
      _calculateRegularBundlePrice() {
        const quantity = this.bundleSize;
        const priceInCents = this._getDataPrice(this);
        const unitPrice = priceInCents / BUNDLE_CONSTANTS.PRICE_DIVISOR;

        return {
          quantity: quantity,
          unitPrice: unitPrice,
          totalPrice: unitPrice * quantity,
          isCustom: false,
          tier: null
        };
      }

      /**
       * Find best price tier for given quantity
       */
      _findBestPriceTier(quantity) {
        let bestMatchItem = null;
        let bestMatchNumber = 0;

        document.querySelectorAll(BUNDLE_CONSTANTS.ALL_BUNDLES_SELECTOR).forEach(candidate => {
          const candidateNumber = this._getDataNumber(candidate);
          if (candidateNumber > 0 && candidateNumber <= quantity && candidateNumber > bestMatchNumber) {
            bestMatchNumber = candidateNumber;
            bestMatchItem = candidate;
          }
        });

        if (bestMatchItem) {
          return {
            quantity: bestMatchNumber,
            price: this._getDataPrice(bestMatchItem)
          };
        }

        return null;
      }

      /**
       * Update price displays with calculated values
       */
      _updatePriceDisplays(priceInfo, currency) {
        // Update custom bundle's own unit price display if applicable
        if (priceInfo.isCustom) {
          if (this.elements.priceDisplay) {
            this.elements.priceDisplay.textContent = `${currency}${priceInfo.unitPrice.toFixed(2)}`;
          }
        }

        // Update all dynamic price elements EXCEPT the one inside custom bundle
        document.querySelectorAll(BUNDLE_CONSTANTS.PRICE_DISPLAY_SELECTOR).forEach(element => {
          // Skip the custom bundle's internal price display
          if (!element.closest(BUNDLE_CONSTANTS.CUSTOM_INPUT_SELECTOR)) {
            element.textContent = `${currency}${priceInfo.totalPrice.toFixed(2)}`;
          }
        });
      }

      updateInputValue() {
        const value = this.bundleSize;
        // selectedQuantity already set at initialization for regular bundles
        if (this.sharedInput) {
          this.sharedInput.value = value;
        }
        if (this.quantityDisplay) {
          this.quantityDisplay.textContent = value;
        }
        this._updateControls();
        this._updateSavings(value);
        this._updateDynamicPrice();
      }

      _updateCustomDefaultValue() {
        if (!this.isCustomBundle) return;

        const maxVisible = this._getMaxBundleQuantity();
        const defaultValue = maxVisible + 1;

        // If current quantity is higher than default but within limits, keep it
        // This preserves enforced values and user selections
        if (this.selectedQuantity > defaultValue && this.selectedQuantity <= this.maxAvailable) {
          return;
        }

        // Only update if value needs to change
        const newValue = Math.min(defaultValue, this.maxAvailable);
        if (this.selectedQuantity !== newValue) {
          // Use the consolidated method
          this.setSelectedQuantity(newValue, {
            updateSharedInput: this.classList.contains(BUNDLE_CONSTANTS.SELECTED_CLASS),
            source: 'auto'
          });
        }
      }

      _setCustomInputHeight() {
        if (!this.elements.customInput) return;

        // Start with collapsed state
        this.elements.customInput.classList.add(BUNDLE_CONSTANTS.COLLAPSED_CLASS);
      }

      _setupQuantityControls() {
        this.buttons = this.querySelectorAll(BUNDLE_CONSTANTS.QUANTITY_BUTTONS_SELECTOR);
        this.quantityDisplay = this.querySelector(BUNDLE_CONSTANTS.QUANTITY_LABEL_SELECTOR);

        this.buttons.forEach(button => {
          button.addEventListener('click', this._onAdjustClick.bind(this));
        });

        // No need for change listener - input is a hidden radio button that users can't manually change

        if (this.quantityDisplay && this.sharedInput) {
          this.quantityDisplay.textContent = this.selectedQuantity;
        }
        this._updateControls();
        this._updateSavings(this.selectedQuantity || 0);
      }

      _onAdjustClick(event) {
        event.stopPropagation();
        const name = event.currentTarget.getAttribute('name');
        let value = this.selectedQuantity || 0;

        if (name === 'plus') {
          this._handleIncrement(value);
        } else if (name === 'minus' && value >= 1) {
          this._handleDecrement(value);
        }
      }

      /**
       * Handle increment button click
       */
      _handleIncrement(currentValue) {
        if (currentValue < this.maxAvailable) {
          this.setSelectedQuantity(currentValue + 1, { source: 'button' });
        } else {
          this._showLimitFeedback();
        }
      }

      /**
       * Handle decrement button click
       */
      _handleDecrement(currentValue) {
        this.setSelectedQuantity(currentValue - 1, { source: 'button' });

        // Hide max message when going below limit (custom bundle only)
        if (this.isCustomBundle) {
          this._hideMaxMessageIfBelowLimit(currentValue - 1);
        }
      }

      /**
       * Hide max message if value is below limit
       */
      _hideMaxMessageIfBelowLimit(value) {
        if (value < this.maxAvailable) {
          this._hideElement(this.elements.maxMessage);
        }
      }

      _showLimitFeedback() {
        this.classList.add(BUNDLE_CONSTANTS.LIMIT_HIT_CLASS);
        setTimeout(() => {
          this.classList.remove(BUNDLE_CONSTANTS.LIMIT_HIT_CLASS);
        }, BUNDLE_CONSTANTS.LIMIT_ANIMATION_DURATION);

        // Show max available message
        if (this.isCustomBundle) {
          // Only for custom selector
          if (this.elements.maxMessage) {
            if (this.elements.maxText) {
              this.elements.maxText.textContent = `Maximum available: ${this.maxAvailable}`;
            }
            this._showElement(this.elements.maxMessage);

            // Clear any existing timeout
            if (this._messageTimeout) {
              clearTimeout(this._messageTimeout);
            }

            // Set new timeout (increase to 5 seconds for better visibility)
            this._messageTimeout = setTimeout(() => {
              this._hideElement(this.elements.maxMessage);
              this._messageTimeout = null;
            }, BUNDLE_CONSTANTS.MESSAGE_DISPLAY_DURATION);
          }
        }
      }

      _updateControls() {
        if (!this.sharedInput) return; // Add this safety check

        const value = this.selectedQuantity || 0;
        this.buttons.forEach(button => {
          if (button.getAttribute('name') === 'minus') {
            button.disabled = value <= 1;
          }
        });
      }

      _updateSavings(selectedQuantity) {
        document.querySelectorAll(BUNDLE_CONSTANTS.ALL_BUNDLES_SELECTOR).forEach(item => {
          const itemNumber = this._getDataNumber(item);
          // Skip custom bundles in this calculation
          if (itemNumber === BUNDLE_CONSTANTS.CUSTOM_BUNDLE) return;

          const currency = this._getDataCurrency(item);

          if (this.classList.contains(BUNDLE_CONSTANTS.CUSTOM_CLASS)) {
            let bestMatchItem = null;
            let bestMatchNumber = 0;

            document.querySelectorAll(BUNDLE_CONSTANTS.ALL_BUNDLES_SELECTOR).forEach(candidate => {
              const candidateNumber = this._getDataNumber(candidate);
              if (candidateNumber > 0 && candidateNumber <= selectedQuantity && candidateNumber > bestMatchNumber) {
                bestMatchNumber = candidateNumber;
                bestMatchItem = candidate;
              }
            });

            if (bestMatchItem) {
              const basePrice = this._getDataPrice(bestMatchItem);
              const finalPrice = this._getDataProductPrice(bestMatchItem);
              const rawSale = ((finalPrice - basePrice) * this.selectedQuantity) / 100;
              // Add rounding tolerance to handle floating point errors
              const sale = Math.abs(rawSale) < 0.01 ? 0 : rawSale; // Treat savings < $0.01 as zero

              const saveSpan = this.elements.savingsDisplay;
              const finalPriceSpan = this.elements.customPriceSpan;
              const savingsWrapper = this.elements.savingsWrapper;
              const customSubtitle = this.elements.subtitle;

              if (saveSpan) {
                const formattedSale =
                  Number(sale.toFixed(2)) % 1 === 0
                    ? `${currency}${Math.floor(sale)}`
                    : `${currency}${sale.toFixed(2)}`;

                saveSpan.textContent = formattedSale;
              }

              if (finalPriceSpan) {
                const roundedUp = Math.ceil(basePrice) / 100;
                finalPriceSpan.textContent = ` ${currency}${roundedUp.toFixed(2)}`;
              }

              if (savingsWrapper) {
                if (sale <= 0) {
                  // Hide for zero or negative savings
                  savingsWrapper.classList.add(BUNDLE_CONSTANTS.HIDDEN_CLASS);
                } else {
                  savingsWrapper.classList.remove(BUNDLE_CONSTANTS.HIDDEN_CLASS);
                }
              }

              if (customSubtitle) {
                const freeShippingThreshold = parseFloat(this.getAttribute('data-free-shipping')) || 0;
                const totalPrice = (basePrice * selectedQuantity) / 100;

                let sourceItem = null;

                if (totalPrice >= freeShippingThreshold) {
                  document.querySelectorAll(BUNDLE_CONSTANTS.ALL_BUNDLES_SELECTOR).forEach(candidate => {
                    const candidateNumber = parseInt(candidate.getAttribute('data-number'), 10);
                    if (
                      candidateNumber > 0 &&
                      candidate.hasAttribute('data-free-shipping-item') &&
                      candidateNumber <= selectedQuantity
                    ) {
                      if (!sourceItem || candidateNumber > this._getDataNumber(sourceItem)) {
                        sourceItem = candidate;
                      }
                    }
                  });
                } else {
                  // Find nearest bundle item WITHOUT data-free-shipping-item attribute
                  document.querySelectorAll(BUNDLE_CONSTANTS.ALL_BUNDLES_SELECTOR).forEach(candidate => {
                    const candidateNumber = parseInt(candidate.getAttribute('data-number'), 10);
                    if (
                      candidateNumber > 0 &&
                      !candidate.hasAttribute('data-free-shipping-item') &&
                      candidateNumber <= selectedQuantity
                    ) {
                      if (!sourceItem || candidateNumber > this._getDataNumber(sourceItem)) {
                        sourceItem = candidate;
                      }
                    }
                  });
                }

                if (sourceItem) {
                  const sourceSubtitle = sourceItem.querySelector(BUNDLE_CONSTANTS.SUBTITLE_SELECTOR);
                  if (sourceSubtitle) {
                    customSubtitle.textContent = sourceSubtitle.textContent;
                  }
                }
              }

              if (this.elements.unitEl) {
                const originalText =
                  this.elements.unitEl.getAttribute('data-original-text') || this.getAttribute('data-unit-names');
                if (!this.elements.unitEl.getAttribute('data-original-text')) {
                  this.elements.unitEl.setAttribute('data-original-text', originalText);
                }

                const packagingTypeSplitted = originalText.split(',').map(s => s.trim());

                let label;
                if (selectedQuantity <= 1) {
                  label = `${packagingTypeSplitted[0]}`;
                } else if (packagingTypeSplitted[1] && packagingTypeSplitted[1] !== '') {
                  label = `${packagingTypeSplitted[1]}`;
                } else {
                  label = `${packagingTypeSplitted[0]}s`;
                }

                this.elements.unitEl.textContent = label;
              }
            }
          } else {
            const savingsWrapper = this.elements.savingsWrapper;
            const finalPriceSpan = this.elements.customPriceSpan;

            if (savingsWrapper) {
              savingsWrapper.classList.add(BUNDLE_CONSTANTS.HIDDEN_CLASS);
            }
            if (finalPriceSpan) {
              const price = this._getDataPrice(item) / 100;
              finalPriceSpan.textContent = `${currency}${price.toFixed(2)}`;
            }
          }
        });
      }

      _updateMaxAvailable() {
        this.maxAvailable = Math.max(0, this.inventoryQuantity - this.cartQuantity);

        // If this is the custom selector, enforce the limit
        if (this.isCustomBundle) {
          this._enforceInventoryLimit();
        }
      }

      _enforceInventoryLimit() {
        if (this.selectedQuantity > this.maxAvailable && this.maxAvailable > 0) {
          // Use the consolidated method to enforce limit
          this.setSelectedQuantity(this.maxAvailable, {
            updateSharedInput: this.classList.contains(BUNDLE_CONSTANTS.SELECTED_CLASS),
            source: 'inventory'
          });
        }
      }

      _updateDynamicBadges() {
        // Only update badges once all bundles are loaded
        const container = this.closest(BUNDLE_CONSTANTS.CONTAINER_SELECTOR);
        if (!container) {
          return;
        }

        // Optimization #1: Cache bundle elements at container level (BEFORE RAF to avoid frame delay)
        if (!container._bundlesCache) {
          container._bundlesCache = Array.from(container.querySelectorAll(BUNDLE_CONSTANTS.ALL_BUNDLES_SELECTOR));
        }

        // Optimization #3: Debounce multiple calls in same tick
        if (container._badgeUpdatePending) return;
        container._badgeUpdatePending = true;

        // Use requestAnimationFrame to batch DOM updates
        requestAnimationFrame(() => {
          container._badgeUpdatePending = false;

          // Remove all existing dynamic badges and their classes
          container.querySelectorAll('.' + BUNDLE_CONSTANTS.BADGE_DYNAMIC_CLASS).forEach(badge => {
            badge.parentElement.classList.remove(BUNDLE_CONSTANTS.HAS_BADGE_CLASS);
            badge.remove();
          });

          // Get all visible bundles (excluding custom) - using cached bundles
          const visibleBundles = container._bundlesCache.filter(bundle => {
            // Check visibility using multiple indicators (avoids forced reflow from getComputedStyle)
            const isVisible =
              !bundle.hasAttribute('aria-hidden') && // Our explicit hiding
              bundle.offsetParent !== null && // Parent/CSS hiding (cheap check)
              bundle.style.display !== 'none'; // Direct style hiding
            const qty = this._getDataNumber(bundle);
            return isVisible && qty > 0; // Visible and not custom
          });

          if (visibleBundles.length === 0) return;

          // Best Deal: DYNAMIC - Always the last visible bundle (moves as bundles hide)
          const bestDeal = visibleBundles[visibleBundles.length - 1];

          // FR-007: Most Popular - FIXED based on TOTAL bundles, not visible bundles
          // Get all bundles (visible + hidden) to calculate the fixed index
          const allBundles = container._bundlesCache.filter(bundle => {
            const qty = this._getDataNumber(bundle);
            return qty > 0; // Exclude custom bundle (qty=0)
          });
          const totalBundles = allBundles.length;

          // Calculate which bundle INDEX should have Most Popular (based on total, not visible)
          let mostPopularIndex = -1;
          if (totalBundles >= 4) {
            mostPopularIndex = 2; // 3rd bundle (0-indexed)
          } else if (totalBundles >= 2) {
            mostPopularIndex = 1; // 2nd bundle (0-indexed)
          }

          // Find if that bundle is visible - if hidden, don't show the badge at all
          let mostPopular = null;
          if (mostPopularIndex >= 0 && mostPopularIndex < allBundles.length) {
            const targetBundle = allBundles[mostPopularIndex];
            // Only show if this specific bundle is visible
            if (visibleBundles.includes(targetBundle)) {
              mostPopular = targetBundle;
            }
          }

          // Add badges using new cleaner class names
          // Best Deal: Only show if the last bundle actually has a lower per-unit price than Bundle 1
          if (bestDeal && visibleBundles.length >= 2) {
            const firstBundle = visibleBundles[0];
            const firstBundlePrice = parseFloat(firstBundle.dataset.price) || 0;
            const bestDealPrice = parseFloat(bestDeal.dataset.price) || 0;

            // Only show Best Deal badge if it's actually cheaper than Bundle 1
            if (bestDealPrice < firstBundlePrice) {
              const badge = document.createElement('div');
              badge.className = `${BUNDLE_CONSTANTS.BADGE_CLASS} ${BUNDLE_CONSTANTS.BADGE_BEST_DEAL_CLASS} ${BUNDLE_CONSTANTS.BADGE_DYNAMIC_CLASS}`;
              // Use color from theme settings if available
              const bestDealColor = container.dataset.bestDealColor;
              if (bestDealColor) {
                badge.style.backgroundColor = bestDealColor;
              }

              const text = document.createElement('p');
              text.className = BUNDLE_CONSTANTS.BADGE_TEXT_CLASS;
              text.textContent = 'BEST DEAL';
              badge.appendChild(text);

              bestDeal.appendChild(badge);
              bestDeal.classList.add(BUNDLE_CONSTANTS.HAS_BADGE_CLASS);
            }
          }

          if (mostPopular && mostPopular !== bestDeal) {
            const badge = document.createElement('div');
            badge.className = `${BUNDLE_CONSTANTS.BADGE_CLASS} ${BUNDLE_CONSTANTS.BADGE_MOST_POPULAR_CLASS} ${BUNDLE_CONSTANTS.BADGE_DYNAMIC_CLASS}`;
            // Use color from theme settings if available
            const mostPopularColor = container.dataset.mostPopularColor;
            if (mostPopularColor) {
              badge.style.backgroundColor = mostPopularColor;
            }

            const text = document.createElement('p');
            text.className = BUNDLE_CONSTANTS.BADGE_TEXT_CLASS;
            text.textContent = 'MOST POPULAR';
            badge.appendChild(text);

            mostPopular.appendChild(badge);
            mostPopular.classList.add(BUNDLE_CONSTANTS.HAS_BADGE_CLASS);
          }
        });
      }
    }

    // Register the custom element
    if (!customElements.get(BUNDLE_CONSTANTS.COMPONENT_NAME)) {
      customElements.define(BUNDLE_CONSTANTS.COMPONENT_NAME, BundleItem);
    }
  } else {
    // ========================================================================
    // FALLBACK PATH - BROWSERS WITHOUT CUSTOM ELEMENTS SUPPORT (IE11, etc.)
    // ========================================================================

    /**
     * Check if user is on Internet Explorer
     */
    const isInternetExplorer = function () {
      const ua = window.navigator.userAgent;
      return ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1;
    };

    /**
     * Initialize fallback for unsupported browsers
     */
    const initializeFallback = function () {
      // Find all bundle items
      const bundleItems = document.querySelectorAll('bundle-item');

      if (bundleItems.length === 0) return;

      // Add warning message
      const warningHtml = `
        <div class="bundle-fallback-warning" style="
          background: #fff3cd;
          border: 1px solid #ffc107;
          color: #856404;
          padding: 12px;
          margin: 20px 0;
          border-radius: 4px;
          text-align: center;
        ">
          <strong>Browser Update Recommended</strong><br>
          For the best shopping experience with bundle discounts, 
          please update to a modern browser like Chrome, Firefox, Safari, or Edge.
        </div>
      `;

      // Insert warning before first bundle item
      const firstBundle = bundleItems[0];
      if (firstBundle.parentNode) {
        firstBundle.parentNode.insertAdjacentHTML('beforebegin', warningHtml);
      }

      // Make bundles visible but non-interactive
      bundleItems.forEach(function (bundle) {
        // Ensure bundle is visible
        bundle.style.display = 'block';
        bundle.style.opacity = '0.7';
        bundle.style.cursor = 'not-allowed';

        // Add visual indicator
        bundle.style.position = 'relative';
        bundle.insertAdjacentHTML(
          'afterbegin',
          `
          <div style="
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255,255,255,0.5);
            z-index: 1;
            pointer-events: none;
          "></div>
        `
        );

        // Disable all interactive elements
        const buttons = bundle.querySelectorAll('button');
        const inputs = bundle.querySelectorAll('input');

        buttons.forEach(function (btn) {
          btn.disabled = true;
          btn.style.cursor = 'not-allowed';
        });

        inputs.forEach(function (input) {
          input.disabled = true;
          input.style.cursor = 'not-allowed';
        });
      });

      // Add fallback "Add to Cart" for single unit
      const singleUnitBundle = document.querySelector('bundle-item[data-number="1"]');
      if (singleUnitBundle) {
        const variantId = singleUnitBundle.getAttribute('data-product-variant-id');

        // Create simple add to cart button
        const fallbackButton = `
          <form action="/cart/add" method="post" style="margin-top: 20px;">
            <input type="hidden" name="id" value="${variantId}">
            <input type="hidden" name="quantity" value="1">
            <button type="submit" class="button button--primary" style="width: 100%;">
              Add Single Item to Cart
            </button>
          </form>
        `;

        // Add after bundles
        const bundleContainer = singleUnitBundle.parentNode;
        if (bundleContainer) {
          bundleContainer.insertAdjacentHTML('afterend', fallbackButton);
        }
      }

      // Log for debugging
      // eslint-disable-next-line no-console
      console.warn('Bundle selector requires Custom Elements support. Fallback mode activated.');

      // Send analytics event if available
      if (window.gtag) {
        window.gtag('event', 'browser_incompatible', {
          event_category: 'compatibility',
          event_label: 'bundle_item_fallback',
          value: isInternetExplorer() ? 'ie' : 'other'
        });
      }
    };

    /**
     * Show browser upgrade prompt (optional)
     */
    const showUpgradePrompt = function () {
      // Only show once per session
      if (sessionStorage.getItem('browserPromptShown')) return;

      const browsers = [
        { name: 'Chrome', url: 'https://www.google.com/chrome/' },
        { name: 'Firefox', url: 'https://www.mozilla.org/firefox/' },
        { name: 'Safari', url: 'https://www.apple.com/safari/' },
        { name: 'Edge', url: 'https://www.microsoft.com/edge' }
      ];

      const browserLinks = browsers
        .map(b => `<a href="${b.url}" target="_blank" style="color: #007bff;">${b.name}</a>`)
        .join(', ');

      const promptHtml = `
        <div id="browser-upgrade-prompt" style="
          position: fixed;
          bottom: 20px;
          right: 20px;
          max-width: 300px;
          background: white;
          border: 2px solid #007bff;
          border-radius: 8px;
          padding: 16px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          z-index: 9999;
        ">
          <button onclick="this.parentElement.remove(); sessionStorage.setItem('browserPromptShown', '1');" 
                  style="position: absolute; top: 5px; right: 10px; border: none; background: none; font-size: 20px; cursor: pointer;">
            ×
          </button>
          <h4 style="margin: 0 0 10px 0;">Upgrade Your Browser</h4>
          <p style="margin: 0 0 10px 0; font-size: 14px;">
            You're missing out on bundle discounts! Upgrade to a modern browser:
          </p>
          <p style="margin: 0; font-size: 14px;">${browserLinks}</p>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', promptHtml);
      sessionStorage.setItem('browserPromptShown', '1');
    };

    /**
     * Initialize fallback on DOM ready
     */
    const init = function () {
      initializeFallback();

      // Show upgrade prompt after 3 seconds for IE users
      if (isInternetExplorer()) {
        setTimeout(showUpgradePrompt, 3000);
      }
    };

    // Run on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    // Expose detection function globally for testing
    window.bundleItemSupport = {
      hasSupport: function () {
        return false;
      },
      isIE: isInternetExplorer,
      fallbackActive: true
    };
  }
})(); // End of IIFE wrapper
